import { useMutation } from "@apollo/client";
import Label from "@mui/icons-material/Label";
import { Button } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ManageTagsFormDialog } from "components/ManageTags/ManageTagsFormDialog";
import { EntityUpdate } from "components/ManageTags/types";
import WasteLineUnifiedTagPicker from "components/WasteLineUnifiedTagPicker";
import pluralize from "pluralize";
import { useCallback, useState } from "react";

import { BULK_UPDATE_PIPELINE_OUTPUT_DATA_MUTATION } from "./api";

const LOCAL_STORAGE_KEY = "pipeline-output-recent-tags";

type PipelineOutputTagRow = {
  id: string;
  tags?: string[] | null;
  reportable?: string | null;
  overrideManualEdits?: string | null;
};

type ManagePipelineOutputTagsDialogProps = {
  selectedRows: PipelineOutputTagRow[];
  tenantId: string;
  dialogOpen: boolean;
  onClose: () => void;
};

export function ManagePipelineOutputTagsButton({
  selectedRowCount,
  onClick,
}: {
  selectedRowCount: number;
  onClick: () => void;
}) {
  return (
    <Button
      startIcon={<Label />}
      onClick={onClick}
      disabled={selectedRowCount === 0}
      size="small"
      variant="outlined"
    >
      Manage Tags
    </Button>
  );
}

export function ManagePipelineOutputTagsDialog({
  selectedRows,
  tenantId,
  dialogOpen,
  onClose,
}: ManagePipelineOutputTagsDialogProps) {
  const alerts = useAlerts();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const [bulkUpdate] = useMutation(BULK_UPDATE_PIPELINE_OUTPUT_DATA_MUTATION);

  const getEntityId = useCallback((row: { id: string }) => row.id, []);

  const handleSubmit = useCallback(
    async (updates: EntityUpdate[]) => {
      const count = updates.length;
      onClose();

      const rowMap = new Map(selectedRows.map((r) => [r.id, r]));

      try {
        await bulkUpdate({
          variables: {
            input: {
              updates: updates.map((u) => ({ id: u.id, fields: { tags: u.tags } })),
            },
          },
          optimisticResponse: {
            bulkUpdatePipelineOutputData: updates.map((u) => {
              const row = rowMap.get(u.id);
              return {
                __typename: "WasteManifestReviewRow" as const,
                id: u.id,
                tags: u.tags,
                reportable: row?.reportable ?? null,
                overrideManualEdits: row?.overrideManualEdits ?? null,
              };
            }),
          },
        });

        alerts.success(`Successfully updated tags for ${count} ${pluralize("row", count)}`);
      } catch (err) {
        alerts.error("An error occurred while updating tags", err);
      }
    },
    [bulkUpdate, alerts, onClose, selectedRows]
  );

  const handleClose = useCallback(() => {
    setShowConfirmation(false);
    onClose();
  }, [onClose]);

  const confirm = useCallback(() => {
    setShowConfirmation(true);
  }, []);

  const onCloseConfirmation = useCallback(() => {
    setShowConfirmation(false);
  }, []);

  if (!dialogOpen) {
    return null;
  }

  return (
    <ManageTagsFormDialog
      selectedRows={selectedRows}
      getEntityId={getEntityId}
      tenantId={tenantId}
      onClose={handleClose}
      onCloseConfirmation={onCloseConfirmation}
      confirm={confirm}
      open={dialogOpen}
      withConfirmation={selectedRows.length > 1}
      showConfirmation={showConfirmation}
      onSubmit={handleSubmit}
      loading={false}
      entityName="Pipeline Output"
      localStorageKey={LOCAL_STORAGE_KEY}
      TagPickerComponent={WasteLineUnifiedTagPicker}
    />
  );
}
